"use client";

import React from "react";
import { Sparkles, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function ProductsListConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    isRefetching,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              data-index={i}
              className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 animate-pulse"
            >
              <div className="aspect-4/5 w-full rounded-xl bg-muted" />
              <div className="space-y-2">
                <div className="h-5 w-3/4 rounded-md bg-muted" />
                <div className="h-4 w-1/2 rounded-md bg-muted" />
                <div className="h-4 w-1/3 rounded-md bg-muted" />
              </div>
              <div className="mt-auto h-10 w-full rounded-full bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center">
        <p className="text-base text-muted-foreground">
          No pudimos cargar los productos en este momento.
        </p>
        <Button
          onClick={() => refetch()}
          variant="outline"
          size="sm"
          className="mt-4 gap-2 rounded-full border-border"
        >
          <RefreshCw className="h-4 w-4" />
          Reintentar
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
        <Sparkles className="h-8 w-8 text-primary mb-3" />
        <p className="font-serif text-lg font-medium text-foreground">
          Sin productos disponibles
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          No encontramos fórmulas con los filtros seleccionados. Intenta ajustar tu búsqueda.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {products.map((product, i) => (
          <article
            key={product.id}
            data-index={i}
            className="group flex flex-col justify-between rounded-2xl border border-border bg-card p-4 transition-[box-shadow,border-color] duration-200 hover:border-primary/40 hover:shadow-md"
          >
            <div>
              <Link
                to={product.link}
                className="block overflow-hidden rounded-xl bg-secondary/50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="aspect-4/5 w-full overflow-hidden">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-secondary text-muted-foreground">
                      <Sparkles className="h-8 w-8 opacity-40" />
                    </div>
                  )}
                </div>
              </Link>

              <div className="mt-4 space-y-2">
                {product.categories && product.categories.length > 0 && (
                  <span className="inline-block rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
                    {product.categories[0].name}
                  </span>
                )}

                <h3 className="font-serif text-lg font-medium text-foreground leading-snug">
                  <Link
                    to={product.link}
                    className="hover:text-primary transition-colors focus:outline-hidden focus-visible:underline"
                  >
                    {product.title}
                  </Link>
                </h3>

                <div className="flex items-baseline gap-2">
                  <ProductPrice
                    product={product}
                    className="text-base font-semibold text-foreground"
                  />
                </div>

                {product.short_description && (
                  <div
                    className="line-clamp-2 text-xs text-muted-foreground [&_p]:mb-0 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: product.short_description }}
                  />
                )}
              </div>
            </div>

            <div className="mt-6 pt-3 border-t border-border/60">
              <AddToCartButton
                product={product}
                productId={product.id}
                variant="default"
                className="w-full"
                buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-full h-11 text-sm font-semibold transition-transform active:scale-[0.98]"
                text="Añadir al carrito"
                showQuantity={false}
              />
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <nav
          role="navigation"
          aria-label="Paginación de productos"
          className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6"
        >
          <p className="text-xs text-muted-foreground">
            Página {currentPage} de {totalPages}
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={!hasPrev || isRefetching}
              className="rounded-full border-border bg-card px-4 text-foreground hover:bg-secondary disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={!hasNext || isRefetching}
              className="rounded-full border-border bg-card px-4 text-foreground hover:bg-secondary disabled:opacity-40"
            >
              Siguiente
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}

export default function Catalog() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as unknown as { wvcClient?: { getCurrentWPQueryParams: () => Record<string, unknown> } }).wvcClient
      ? (window as unknown as { wvcClient: { getCurrentWPQueryParams: () => Record<string, unknown> } }).wvcClient.getCurrentWPQueryParams()
      : {};

  const queryInstance = React.useMemo(() => new WP_Query(currentWPQueryParams), [currentWPQueryParams]);

  return (
    <section
      id="product-listings"
      data-nav="light"
      className="bg-background py-10 md:py-16 text-foreground"
    >
      <div className="mx-auto max-w-[1240px] px-5 md:px-8 lg:px-14">
        <EcommerceProductsProvider wp_query={queryInstance}>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start">
            <aside className="w-full lg:w-1/4 shrink-0">
              <div className="sticky top-24 rounded-2xl border border-border bg-card p-5 md:p-6">
                <ProductsFilter />
              </div>
            </aside>

            <div className="w-full lg:w-3/4 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card/60 p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Fórmulas e ingredientes botánicos
                </p>
                <div className="flex items-center gap-2">
                  <ProductSortDropdown label="Ordenar por:" />
                </div>
              </div>

              <ProductsListConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}